import React from 'react';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';

import {
  inputAcceptTransmitDataConfig,
  inputBirthcountryConfig,
  inputBirthdateConfig,
  inputBirthplaceConfig,
  inputEmailConfig,
  inputEmailValidConfig,
  inputFileUploadConfig,
  inputTextareaDescriptionConfig,
} from '../../../configs';
import { PageLayoutContext, ReactivationFormValues } from '../../../interfaces';
import { personGroupDecorator } from '../../../libs/decorators';
import { FileInputLabel, FormName, FormType } from '../../../libs/enums';
import {
  CheckboxComponent,
  FileUploadComponent,
  TextAreaComponent,
  TextInputComponent,
} from '../../dsfr';
import { ErrorCodeComponent } from '../error-code';
import { FormWrapperComponent } from '../form-wrapper';
import { PersonGroup } from '../person-group';

interface RouteParams extends Record<string, string | undefined> {
  errorCode: string;
}

export const ReactivationForm = React.memo(() => {
  const { errorCode } = useParams<RouteParams>();
  const { errorId, platform } = useOutletContext<PageLayoutContext>();

  const memoizedDescription = React.useMemo(
    () => (
      <React.Fragment>
        Pour faciliter la prise en charge de votre demande, veuillez nous transmettre le message de
        confirmation de la désactivation de l’accès FranceConnect envoyé par FranceConnect.
        <br />
        <br />
        Taille maximale : 2Mo. Les formats supportés sont : JPG, JPEG, PNG, GIF, DOCX, ODT, PDF.
      </React.Fragment>
    ),
    [],
  );

  if (!errorCode) {
    return (
      <Navigate replace state={{ error: `${FormType.REACTIVATION}/${errorCode}` }} to="/error" />
    );
  }

  return (
    <FormWrapperComponent<ReactivationFormValues>
      decorators={[personGroupDecorator]}
      initialValues={{
        acceptTransmitData: undefined,
        birthcountry: undefined,
        birthdate: undefined,
        birthplace: undefined,
        email: undefined,
        emailValid: undefined,
        errorCode,
        errorId,
        firstName: undefined,
        firstNameDisabled: false,
        formName: FormName.REACTIVATION,
        formType: FormType.REACTIVATION,
        lastName: undefined,
        lastNameDisabled: false,
        platform,
        textareaDescription: undefined,
      }}>
      <React.Fragment>
        <div className="fr-mb-3w">
          <ErrorCodeComponent />
        </div>
        <PersonGroup />
        <TextInputComponent {...inputBirthdateConfig} />
        <TextInputComponent {...inputBirthcountryConfig} />
        <TextInputComponent {...inputBirthplaceConfig} />
        <TextInputComponent {...inputEmailConfig} />
        <TextInputComponent {...inputEmailValidConfig} />
        <TextAreaComponent {...inputTextareaDescriptionConfig} />
        <FileUploadComponent
          {...inputFileUploadConfig}
          description={memoizedDescription}
          label={FileInputLabel.SCREEN}
          required={false}
        />
        <CheckboxComponent {...inputAcceptTransmitDataConfig} />
      </React.Fragment>
    </FormWrapperComponent>
  );
});

ReactivationForm.displayName = 'ReactivationForm';
